/**
 * Squad page tab nav — sticky link-based tabs mirroring the /matches page.
 * Preserves unrelated search params on navigation.
 */

"use client";

import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { cn } from "@/utilities/core/cn";
import type { SquadTab } from "../SquadPage";

interface SquadTabNavProps {
    tab: SquadTab;
    labels: { players: string; staff: string };
}

export function SquadTabNav({ tab, labels }: SquadTabNavProps) {
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const buildHref = (nextTab: SquadTab) => {
        const params = new URLSearchParams(searchParams?.toString() ?? "");
        if (nextTab === "players") params.delete("tab");
        else params.set("tab", nextTab);
        const query = params.toString();
        return query ? `${pathname}?${query}` : pathname;
    };

    return (
        <nav className="py-0.5 tab-bar-surface sticky top-below-nav z-10">
            <div className="max-w-350 mx-auto px-4">
                <div className="flex gap-2 my-2 overflow-x-auto scrollbar-hide">
                    {(["players", "staff"] as const).map((key) => (
                        <Link
                            key={key}
                            href={buildHref(key)}
                            prefetch={false}
                            className={cn(
                                "px-5 py-3 font-medium text-xs uppercase whitespace-nowrap",
                                tab === key ? "btn-primary" : "link-themed"
                            )}
                        >
                            {labels[key]}
                        </Link>
                    ))}
                </div>
            </div>
        </nav>
    );
}
